import React, { useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";

// Initial seed values (forex + synthetic indices use simulated micro-walks)
const SEED = [
  { symbol: "BTC/USD",     price: 96420.50, decimals: 2, vol: 0.0008, ws: "BTC-USD"  },
  { symbol: "ETH/USD",     price: 3320.18,  decimals: 2, vol: 0.0010, ws: "ETH-USD"  },
  { symbol: "SOL/USD",     price: 195.73,   decimals: 2, vol: 0.0014, ws: "SOL-USD"  },
  { symbol: "XRP/USD",     price: 2.245,    decimals: 4, vol: 0.0015, ws: "XRP-USD"  },
  { symbol: "ADA/USD",     price: 0.9675,   decimals: 4, vol: 0.0016, ws: "ADA-USD"  },
  { symbol: "DOGE/USD",    price: 0.3825,   decimals: 4, vol: 0.0018, ws: "DOGE-USD" },
  { symbol: "EUR/USD",     price: 1.0892,   decimals: 4, vol: 0.0002 },
  { symbol: "GBP/USD",     price: 1.2674,   decimals: 4, vol: 0.0003 },
  { symbol: "USD/JPY",     price: 154.31,   decimals: 2, vol: 0.0003 },
  { symbol: "XAU/USD",     price: 2655.42,  decimals: 2, vol: 0.0006 },
  { symbol: "Step Index",  price: 8742.55,  decimals: 2, vol: 0.0012 },
  { symbol: "Boom 1000",   price: 12435.10, decimals: 2, vol: 0.0018 },
  { symbol: "Crash 500",   price: 4567.90,  decimals: 2, vol: 0.0020 },
  { symbol: "US30",        price: 44128.40, decimals: 2, vol: 0.0005 },
  { symbol: "NAS100",      price: 21054.78, decimals: 2, vol: 0.0007 },
];

// Coinbase WebSocket — globally accessible, no API key required.
const COINBASE_WS_URL = "wss://ws-feed.exchange.coinbase.com";
const WS_PRODUCT_IDS = SEED.filter((s) => s.ws).map((s) => s.ws);

const fmt = (v, decimals) =>
  v.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

export default function MarketTicker() {
  const [items, setItems] = useState(() => SEED.map((s) => ({ ...s, prev: s.price, dir: 0 })));
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const tickRef = useRef(null);
  const reconnectRef = useRef(null);

  // ─── Coinbase WebSocket (live crypto prices) ───────────────────────────
  useEffect(() => {
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      try {
        const ws = new WebSocket(COINBASE_WS_URL);
        wsRef.current = ws;

        ws.onopen = () => {
          if (cancelled) return;
          ws.send(JSON.stringify({
            type: "subscribe",
            product_ids: WS_PRODUCT_IDS,
            channels: ["ticker"],
          }));
          setWsConnected(true);
        };

        ws.onmessage = (ev) => {
          if (cancelled) return;
          try {
            const data = JSON.parse(ev.data);
            if (data.type !== "ticker" || !data.product_id) return;
            const price = parseFloat(data.price);
            const open24h = parseFloat(data.open_24h);
            if (!Number.isFinite(price)) return;
            setItems((prev) =>
              prev.map((it) => {
                if (it.ws !== data.product_id) return it;
                const dir = price > it.price ? 1 : price < it.price ? -1 : it.dir;
                return { ...it, prev: open24h || it.price, price, dir };
              })
            );
          } catch (_) { /* ignore */ }
        };

        ws.onclose = () => {
          if (cancelled) return;
          setWsConnected(false);
          reconnectRef.current = setTimeout(connect, 3000);
        };

        ws.onerror = () => {
          try { ws.close(); } catch (_) { /* ignore */ }
        };
      } catch (_) {
        if (!cancelled) reconnectRef.current = setTimeout(connect, 5000);
      }
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) { /* ignore */ }
      }
    };
  }, []);

  // ─── Simulated micro-fluctuations for non-WS instruments ────────────────
  useEffect(() => {
    tickRef.current = setInterval(() => {
      setItems((prev) =>
        prev.map((it) => {
          if (it.ws) return it; // crypto handled by WS
          const drift = (Math.random() - 0.5) * 2 * it.vol;
          const next = Math.max(0, it.price * (1 + drift));
          const dir = next > it.price ? 1 : next < it.price ? -1 : it.dir;
          return { ...it, prev: it.price, price: next, dir };
        })
      );
    }, 1500);
    return () => clearInterval(tickRef.current);
  }, []);

  const stream = [...items, ...items];

  return (
    <div className="border-t border-white/[0.06] bg-[#08080F] overflow-hidden relative" data-testid="market-ticker">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1.5 bg-[#08080F]/95 pr-3 backdrop-blur-sm">
        <span
          className={`h-1.5 w-1.5 rounded-full ${wsConnected ? "bg-emerald-400 animate-pulse" : "bg-amber-400"}`}
          data-testid="ticker-live-dot"
        />
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {wsConnected ? "LIVE" : "SIM"}
        </span>
      </div>

      <div className="flex ticker-track gap-10 py-3 whitespace-nowrap pl-20">
        {stream.map((it, i) => {
          const up = it.dir > 0;
          const down = it.dir < 0;
          const Arrow = up ? ArrowUp : ArrowDown;
          const tone = up ? "text-emerald-400" : down ? "text-red-400" : "text-zinc-500";
          const pct = it.prev ? ((it.price - it.prev) / it.prev) * 100 : 0;
          return (
            <span
              key={`${it.symbol}-${i}`}
              className="inline-flex items-center gap-2 text-xs font-mono tracking-wider"
              data-testid={`ticker-item-${i % SEED.length}`}
            >
              <span className="text-zinc-300">{it.symbol}</span>
              <span className="text-zinc-100 font-semibold">{fmt(it.price, it.decimals)}</span>
              {(up || down) && (
                <span className={`inline-flex items-center gap-0.5 ${tone}`}>
                  <Arrow className="h-3 w-3" />
                  <span>{Math.abs(pct).toFixed(2)}%</span>
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
